import { SystemRoute } from './components/SystemPage';
import { CockpitPanelProvider } from './components/cockpit/SidePanel';
import { GUEST_REPLY_SETTINGS_ROLES } from './config/tenant';
import { TASK_MANAGEMENT_ROLES } from './config/dashboardRoles';
import React, { useEffect, lazy, Suspense } from 'react';
import { initializeDateErrorHandling } from './utils/globalDateErrorHandler';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TenantProvider } from './contexts/TenantContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { PrivateRoute } from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { SkipNavigation } from './components/SkipNavigation';
import { AccessibilityProvider } from './components/accessibility/AccessibilityProvider';
import { Navigation } from './components/Navigation';
import { CellFeaturesNotice } from './components/CellFeaturesNotice';
import { CellFeaturesProvider, useCellFeatures } from './contexts/CellFeaturesContext';

/**
 * REQUIREMENT: FRN-001 - UI Accessibility
 * 
 * STATEMENT: "The UI shall provide keyboard navigation, focus management, and ARIA-compliant error announcements with WCAG 2.1 AA compliance"
 * 
 * TRACES TO:
 * - Implementation: This file
 * - Tests: tests/App.test.tsx
 * - Interface: se/interfaces/openapi.yaml
 */


// Lazy load pages for code splitting and performance
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const CleaningManagement = lazy(() => import('./pages/CleaningManagement'));
const TaskManagement = lazy(() => import('./pages/TaskManagement'));
const Messages = lazy(() => import('./pages/Messages'));
const RulesManagement = lazy(() => import('./pages/RulesManagement').then(m => ({ default: m.RulesManagement })));
const VoicebotConfiguration = lazy(() => import('./pages/VoicebotConfiguration').then(m => ({ default: m.VoicebotConfiguration })));
const FaqManagement = lazy(() => import('./pages/FaqManagement'));
const CalendarView = lazy(() => import('./pages/CalendarView').then(m => ({ default: m.CalendarView })));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Landing = lazy(() => import('./pages/Landing'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const BookingManagement = lazy(() => import('./pages/BookingManagement').then(m => ({ default: m.BookingManagement })));
const GuestReplySettings = lazy(() => import('./pages/GuestReplySettings').then(m => ({ default: m.GuestReplySettings })));
const MailboxSettings = lazy(() => import('./pages/MailboxSettings').then(m => ({ default: m.MailboxSettings })));
const Pricing = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })));
const FeedbackDashboard = lazy(() => import('./pages/FeedbackDashboard'));
const DataExplorer = lazy(() => import('./pages/DataExplorer'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Impressum = lazy(() => import('./pages/Impressum'));
import { useAuth } from './contexts/AuthContext';
import { isRouteEnabled, peekTenantRuntime } from './config/tenant';

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" role="status" aria-label="Loading">
      <span className="sr-only">Loading...</span>
    </div>
  </div>
);

export function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  // sepipe: REQ-HotelTenancy-1158 Cell features come from the backend at runtime.
  const features = useCellFeatures();

  return (
    <CockpitPanelProvider routeKey={location.key} key={`${user?.uid ?? 'guest'}:${peekTenantRuntime()?.tenantId}`}><div className="h-[100dvh] bg-bg text-ink flex flex-col">
      {user && <Navigation />}
      <main id="main-content" className={`min-h-0 min-w-0 flex-1 flex flex-col overflow-auto relative pb-[calc(48px+env(safe-area-inset-bottom))] md:pb-0 ${user ? 'lg:ml-52' : ''}`} tabIndex={-1}>
        <CellFeaturesNotice />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={user ? (
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            ) : (
              <Landing />
            )} />
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/settings/pricing" element={<PrivateRoute requiredRole={['owner', 'admin', 'manager']}><Pricing key={peekTenantRuntime()?.tenantId} /></PrivateRoute>} />
            <Route path="/bookings/:id?" element={
              <PrivateRoute>
                <div className="booking-route md:flex md:min-h-0 md:flex-1 md:flex-col"><BookingManagement /></div>
              </PrivateRoute>
            } />
            <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
            <Route path="/emails/:emailId?" element={<PrivateRoute><MessagesRedirect view="emails" /></PrivateRoute>} />
            <Route path="/support" element={<PrivateRoute><MessagesRedirect view="offen" /></PrivateRoute>} />
            <Route path="/system/email-analysis" element={<Navigate to="/emails" replace />} />
            {isRouteEnabled('/settings/replies', features) && (
              <Route path="/settings/replies" element={
                <PrivateRoute requiredRole={GUEST_REPLY_SETTINGS_ROLES}>
                  <GuestReplySettings />
                </PrivateRoute>
              } />
            )}
            {isRouteEnabled('/settings/mailbox', features) && (
              <Route path="/settings/mailbox" element={
                <PrivateRoute requiredRole={['manager', 'admin']}>
                  <MailboxSettings />
                </PrivateRoute>
              } />
            )}
            {isRouteEnabled('/alerts', features) && <Route path="/alerts" element={<Navigate to="/settings/team" replace />} />}
            <Route path="/cleaning" element={<PrivateRoute><CleaningManagement /></PrivateRoute>} />
            {isRouteEnabled('/tasks', features) && <Route path="/tasks" element={
              <PrivateRoute requiredRole={TASK_MANAGEMENT_ROLES}>
                <div><TaskManagement /></div>
              </PrivateRoute>
            } />}
            {isRouteEnabled('/settings/team', features) && (
              <Route path="/settings/team" element={
                <PrivateRoute requiredRole={['manager', 'admin']}>
                  <div><NotificationSettings /></div>
                </PrivateRoute>
              } />
            )}
            {!isRouteEnabled('/tasks', features) && <Route path="/tasks" element={
              <PrivateRoute requiredRole={TASK_MANAGEMENT_ROLES}><TaskManagement /></PrivateRoute>
            } />}
            {!isRouteEnabled('/settings/team', features) && <Route path="/settings/team" element={
              <PrivateRoute requiredRole={['manager', 'admin']}><NotificationSettings /></PrivateRoute>
            } />}
            {!isRouteEnabled('/alerts', features) && <Route path="/alerts" element={<PrivateRoute requiredRole={['manager', 'admin']}><Navigate to="/settings/team" replace /></PrivateRoute>} />}
                        <Route path="/feedback" element={
              <PrivateRoute requiredRole={['manager', 'admin']}>
                <div><FeedbackDashboard /></div>
              </PrivateRoute>
            } />
            {isRouteEnabled('/calendar', features) && <Route path="/calendar" element={
              <PrivateRoute>
                <div className="flex min-h-0 flex-1 flex-col"><CalendarView /></div>
              </PrivateRoute>
            } />}
            <Route path="/rules" element={
              <PrivateRoute requiredRole={['manager', 'admin']}>
                <div className="py-6"><RulesManagement /></div>
              </PrivateRoute>
            } />
            {isRouteEnabled('/voicebot', features) && (
              <Route path="/voicebot" element={
                <PrivateRoute requiredRole={['manager', 'admin']}>
                  <div className="py-6"><VoicebotConfiguration /></div>
                </PrivateRoute>
              } />
            )}
            {isRouteEnabled('/faq', features) && (
              <Route path="/faq" element={
                <PrivateRoute requiredRole={['manager', 'admin']}>
                  <div className="py-6"><FaqManagement /></div>
                </PrivateRoute>
              } />
            )}
            <Route path="/monitoring" element={
              <PrivateRoute>
                <SystemRoute path="/monitoring" title="Monitoring"><div><MonitoringDashboard /></div></SystemRoute>
              </PrivateRoute>
            } />
            <Route path="/data-explorer" element={
              <PrivateRoute>
                <SystemRoute path="/data-explorer" title="Datenexplorer"><div><DataExplorer /></div></SystemRoute>
              </PrivateRoute>
            } />
            <Route path="/users" element={
              <PrivateRoute>
                <SystemRoute path="/users" title="Benutzer"><div><UserManagement /></div></SystemRoute>
              </PrivateRoute>
            } />
            {/* Public legal pages */}
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/agb" element={<TermsOfService />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
        </Suspense>
      </main>
    </div></CockpitPanelProvider>
  );
}

function MessagesRedirect({ view }: { view: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', location.pathname === '/support' && params.has('session') ? 'ohne-buchung' : view);
  const id = location.pathname.split('/')[2];
  if (id && location.pathname.startsWith('/emails/')) params.set('email', decodeURIComponent(id));
  return <Navigate to={`/messages?${params}`} replace />;
}

function App() {
  useEffect(() => {
    // Initialize global date error handling
    initializeDateErrorHandling();
    console.log('Date error handling initialized');
  }, []);

  return (
    <Router>
      <ErrorBoundary name="App-Root">
        <ToastProvider>
          <AuthProvider>
            <TenantProvider>
            <CellFeaturesProvider>
              <AccessibilityProvider>
                <SkipNavigation />
                <ErrorBoundary name="App-Content">
                  <AppContent />
                </ErrorBoundary>
              </AccessibilityProvider>
            </CellFeaturesProvider>
            </TenantProvider>
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
